// src/components/ProfileTasksBoard.jsx
import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Badge,
  Row,
  Col,
  Spinner,
  Alert
} from 'react-bootstrap';
import { useTasks } from '../contexts/TasksContext';
import { useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';

// Компонент карточки задачи для канбан-доски профиля
const ProfileBoardTaskCard = ({ task, onView, getStatusBadgeVariant, getPriorityBadgeVariant }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'task',
    item: { id: task.id, status: task.status },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  const getStatusText = (status) => {
    const statusMap = {
      'open': 'Открыта',
      'assigned': 'Назначена',
      'in_progress': 'В работе',
      'completed': 'Выполнена',
      'cancelled': 'Отменена',
      'reopened': 'Переоткрыта'
    };
    return statusMap[status] || status;
  };

  const getPriorityText = (priority) => {
    const priorityMap = {
      'low': 'Низкий',
      'medium': 'Средний',
      'high': 'Высокий',
      'urgent': 'Срочный'
    };
    return priorityMap[priority] || priority;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && 
                   !['completed', 'cancelled'].includes(task.status);

  return (
    <div
      ref={drag}
      className={`board-task-card ${isDragging ? 'dragging' : ''}`}
      onClick={() => onView(task)}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: 'pointer'
      }}
    >
      <Card className="h-100 task-card">
        <Card.Body className="p-3">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <Badge bg={getStatusBadgeVariant(task.status)} className="mb-1">
              {getStatusText(task.status)}
            </Badge>
            <Badge bg={getPriorityBadgeVariant(task.priority)}>
              {getPriorityText(task.priority)}
            </Badge>
          </div>
          
          <h6 className="task-title mb-2" title={task.title}>
            {task.title}
          </h6>
          
          {task.description && (
            <p className="task-description text-muted small mb-2">
              {task.description.length > 80 
                ? `${task.description.substring(0, 80)}...` 
                : task.description
              }
            </p>
          )}
          
          <div className="task-meta small">
            {task.due_date && (
              <div className={`mb-1 ${isOverdue ? 'text-danger' : 'text-muted'}`}>
                <i className="bi bi-calendar me-1"></i>
                {formatDate(task.due_date)}
                {isOverdue && <span className="ms-1">⚠️</span>}
              </div>
            )}
            
            {task.estimated_hours && (
              <div className="text-muted">
                <i className="bi bi-clock me-1"></i>
                {task.estimated_hours}ч
              </div>
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

// Компонент колонки канбан-доски профиля
const ProfileBoardColumn = ({ status, tasks, onTaskDrop, onView, getStatusBadgeVariant, getPriorityBadgeVariant }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'task',
    drop: (item) => onTaskDrop(item.id, status),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }));

  const getStatusText = (status) => {
    const statusMap = {
      'open': 'Открытые',
      'assigned': 'Назначенные',
      'in_progress': 'В работе',
      'completed': 'Выполненные',
      'cancelled': 'Отмененные',
      'reopened': 'Переоткрытые'
    };
    return statusMap[status] || status;
  };

  const getStatusIcon = (status) => {
    const iconMap = {
      'open': 'bi-circle',
      'assigned': 'bi-person',
      'in_progress': 'bi-play-circle',
      'completed': 'bi-check-circle',
      'cancelled': 'bi-x-circle',
      'reopened': 'bi-arrow-repeat'
    };
    return iconMap[status] || 'bi-circle';
  };

  return (
    <div 
      ref={drop}
      className={`board-column ${isOver ? 'drag-over' : ''}`}
      style={{
        backgroundColor: isOver ? 'rgba(141, 192, 251, 0.1)' : 'transparent',
        minHeight: '400px'
      }}
    >
      <Card className="h-100">
        <Card.Header className="d-flex align-items-center">
          <i className={`bi ${getStatusIcon(status)} me-2`}></i>
          <span className="fw-semibold">{getStatusText(status)}</span>
          <Badge bg="secondary" className="ms-2">{tasks.length}</Badge>
        </Card.Header>
        <Card.Body className="p-3">
          <div className="tasks-column">
            {tasks.map(task => (
              <ProfileBoardTaskCard
                key={task.id}
                task={task}
                onView={onView}
                getStatusBadgeVariant={getStatusBadgeVariant}
                getPriorityBadgeVariant={getPriorityBadgeVariant}
              />
            ))}
            
            {tasks.length === 0 && (
              <div className="text-center text-muted py-4">
                <i className="bi bi-inbox display-6 opacity-50"></i>
                <p className="mt-2 mb-0">Нет задач</p>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

// Основной компонент канбан-доски для профиля
const ProfileTasksBoard = ({ 
  tasks,
  onView, 
  getStatusBadgeVariant, 
  getPriorityBadgeVariant,
  onTaskUpdate
}) => {
  const { updateTask } = useTasks();
  const [groupedTasks, setGroupedTasks] = useState({});

  useEffect(() => {
    if (tasks.length > 0) {
      groupTasksByStatus();
    }
  }, [tasks]);

  const groupTasksByStatus = () => {
    const grouped = {
      'open': [],
      'assigned': [],
      'in_progress': [],
      'completed': [],
      'cancelled': [],
      'reopened': []
    };

    tasks.forEach(task => {
      if (grouped[task.status]) {
        grouped[task.status].push(task);
      } else {
        grouped['open'].push(task);
      }
    });

    // Сортируем задачи по дате создания (новые сверху)
    Object.keys(grouped).forEach(status => {
      grouped[status].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    });

    setGroupedTasks(grouped);
  };

  const handleTaskDrop = async (taskId, newStatus) => {
    try {
      await updateTask(taskId, { status: newStatus });
      // После обновления перезагружаем задачи
      if (onTaskUpdate) {
        onTaskUpdate();
      }
    } catch (err) {
      console.error('Error updating task status:', err);
    }
  };

  const statusColumns = [
    { key: 'open', title: 'Открытые', icon: 'bi-circle' },
    // { key: 'assigned', title: 'Назначенные', icon: 'bi-person' },
    { key: 'in_progress', title: 'В работе', icon: 'bi-play-circle' },
    { key: 'completed', title: 'Выполненные', icon: 'bi-check-circle' },
    { key: 'cancelled', title: 'Отмененные', icon: 'bi-x-circle' },
    // { key: 'reopened', title: 'Переоткрытые', icon: 'bi-arrow-repeat' }
  ];

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="profile-tasks-board">
        <div className="board-container">
          <Row className="g-3">
            {statusColumns.map(column => (
              <Col key={column.key} xl={3} lg={4} md={6}>
                <ProfileBoardColumn
                  status={column.key}
                  tasks={groupedTasks[column.key] || []}
                  onTaskDrop={handleTaskDrop}
                  onView={onView}
                  getStatusBadgeVariant={getStatusBadgeVariant}
                  getPriorityBadgeVariant={getPriorityBadgeVariant}
                />
              </Col>
            ))}
          </Row>
        </div>

        <div className="board-footer mt-4">
          <Card className="bg-light">
            <Card.Body className="py-3">
              <div className="d-flex justify-content-between align-items-center">
                <div className="text-muted small">
                  <i className="bi bi-info-circle me-1"></i>
                  Всего моих задач: <strong>{tasks.length}</strong>
                </div>
                <div className="text-muted small">
                  Перетащите задачу в другую колонку для изменения статуса
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    </DndProvider>
  );
};

export default ProfileTasksBoard;